import React, { createContext, useContext, useState, useCallback } from 'react';

const CartContext = createContext();

function makeCartKey(base) {
  return `${base}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function sameModifiers(left = [], right = []) {
  return JSON.stringify(left || []) === JSON.stringify(right || []);
}

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [activeOrderId, setActiveOrderId] = useState(null);

  // modifiers: [{name, price_delta}] — optional. Items with modifiers are always added as new lines.
  const addItem = useCallback((product, modifiers = [], itemMeta = {}) => {
    const modifierTotal = modifiers.reduce((sum, m) => sum + (m.price_delta || 0), 0);
    const unitPrice = product.price + modifierTotal;
    const course = itemMeta.course || 'Sofort';
    const sendImmediately = itemMeta.sendImmediately ?? course === 'Sofort';
    const menuItemId = product.__menu_item_id || product.menu_item_id || '';
    const menuId = product.__menu_id || product.menu_id || '';
    const menuGroupId = product.__menu_group_id || product.menu_group_id || '';
    const cartIdentity = product.__menu_key || product.cart_identity || product.id;

    setItems((current) => {
      // Only merge into still-unsent lines. Sent kitchen lines stay immutable.
      if (modifiers.length === 0) {
        const existingIndex = current.findIndex(
          (item) =>
            !item.isSent &&
            item.product_id === product.id &&
            (item.menu_item_id || '') === menuItemId &&
            (item.course || 'Sofort') === course &&
            (item.send_immediately !== false) === sendImmediately &&
            (!item.modifiers || item.modifiers.length === 0)
        );
        if (existingIndex >= 0) {
          const updated = [...current];
          updated[existingIndex] = {
            ...updated[existingIndex],
            quantity: updated[existingIndex].quantity + 1,
            total: (updated[existingIndex].quantity + 1) * updated[existingIndex].price,
          };
          return updated;
        }
      }

      const cartKey = modifiers.length > 0 || activeOrderId
        ? makeCartKey(cartIdentity)
        : cartIdentity;

      return [
        ...current,
        {
          product_id: product.id,
          cartKey,
          cart_identity: cartIdentity,
          product_name: product.name,
          price: unitPrice,
          quantity: 1,
          vat_rate: product.vat_rate || 10,
          total: unitPrice,
          modifiers,
          print_group: (product.print_groups || [])[0] || '',
          course,
          send_immediately: sendImmediately,
          notes: '',
          status: 'pending',
          menu_id: menuId,
          menu_item_id: menuItemId,
          menu_group_id: menuGroupId,
          isSent: false,
        },
      ];
    });
  }, [activeOrderId]);

  // cartKey: product_id for plain items, or a unique key for modifier items
  const removeItem = useCallback((cartKey) => {
    setItems((current) => {
      const idx = current.findIndex(item => (item.cartKey || item.product_id) === cartKey);
      if (idx < 0) return current;
      return current.filter((_, i) => i !== idx);
    });
  }, []);

  const updateQuantity = useCallback((cartKey, delta) => {
    setItems((current) => {
      const targetIndex = current.findIndex((item) => (item.cartKey || item.product_id) === cartKey);
      if (targetIndex < 0) return current;

      const target = current[targetIndex];

      // Already-sent kitchen lines are immutable:
      // "+" creates a new pending line, "-" must not silently reduce the sent quantity.
      if (target.isSent) {
        if (delta <= 0) return current;

        const pendingMatchIndex = current.findIndex((item) =>
          item.product_id === target.product_id &&
          (item.menu_item_id || '') === (target.menu_item_id || '') &&
          !item.isSent &&
          item.price === target.price &&
          (item.course || 'Sofort') === (target.course || 'Sofort') &&
          (item.send_immediately !== false) === (target.send_immediately !== false) &&
          sameModifiers(item.modifiers, target.modifiers)
        );

        if (pendingMatchIndex >= 0) {
          const updated = [...current];
          const pendingItem = updated[pendingMatchIndex];
          updated[pendingMatchIndex] = {
            ...pendingItem,
            quantity: pendingItem.quantity + delta,
            total: (pendingItem.quantity + delta) * pendingItem.price,
          };
          return updated;
        }

        const pendingClone = {
          ...target,
          cartKey: makeCartKey(target.cart_identity || target.product_id),
          quantity: delta,
          total: target.price * delta,
          status: 'pending',
          isSent: false,
        };

        return [
          ...current.slice(0, targetIndex + 1),
          pendingClone,
          ...current.slice(targetIndex + 1),
        ];
      }

      return current
        .map((item) => {
          if ((item.cartKey || item.product_id) !== cartKey) return item;
          const newQuantity = Math.max(0, item.quantity + delta);
          if (newQuantity === 0) return null;
          return { ...item, quantity: newQuantity, total: newQuantity * item.price };
        })
        .filter(Boolean);
    });
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setSelectedTable(null);
    setActiveOrderId(null);
  }, []);

  // Load an existing order into the cart (for continue-order and Zahlen flows)
  const loadOrder = useCallback((order) => {
    const normalizedItems = (order.items || [])
      .filter((i) => !i.voided)
      .map((i) => ({
        product_id: i.product_id,
        // Must match the backend's identity fallback (routers/pos.py _item_identity)
        // exactly, or voiding an already-sent item won't find its match server-side.
        cartKey: i.cartKey || i.product_id,
        product_name: i.product_name,
        price: i.price,
        quantity: i.quantity,
        vat_rate: i.vat_rate || 10,
        total: i.price * i.quantity,
        modifiers: i.modifiers || [],
        print_group: i.print_group || '',
        course: i.course || 'Sofort',
        send_immediately: i.send_immediately !== false,
        notes: i.notes || '',
        status: i.status || 'pending',
        menu_id: i.menu_id || '',
        menu_item_id: i.menu_item_id || '',
        menu_group_id: i.menu_group_id || '',
        isSent: i.isSent !== false,
      }));
    setItems(normalizedItems);
    if (order.table_id) {
      setSelectedTable({ id: order.table_id, name: order.table_name });
    }
    setActiveOrderId(order.id);
  }, []);

  const subtotal = items.reduce((sum, item) => sum + item.total, 0);
  const vatTotal = items.reduce((sum, item) => {
    return sum + (item.total - item.total / (1 + item.vat_rate / 100));
  }, 0);
  const total = subtotal;

  const value = {
    items,
    selectedTable,
    setSelectedTable,
    activeOrderId,
    setActiveOrderId,
    loadOrder,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    subtotal: Math.round(subtotal * 100) / 100,
    vatTotal: Math.round(vatTotal * 100) / 100,
    total: Math.round(total * 100) / 100,
    itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
